import { Capacitor } from "@capacitor/core";

import { safeConsole } from "@/utils/safeConsole";
// Type-only: erased at build, so this creates no runtime edge to the native
// module and none of its imports reach the entry chunk.
import type { PushContext } from "./pushNotificationsNative";

/**
 * Web-safe entry point for dispatch push notifications (SD-13244).
 *
 * The real implementation lives in `pushNotificationsNative.ts` and is reached
 * only through the gated `import()` below. That split is load-bearing for two
 * reasons:
 *
 * 1. **The unhandled rejection.** Capacitor's `registerPlugin` proxy resolves
 *    its web implementation on the FIRST METHOD CALL, not at import — so the
 *    trigger was `revokeRegisteredDispatchPushDevice`'s
 *    `FirebaseMessaging.deleteToken()`. Loading that implementation constructs
 *    `FirebaseMessagingWeb`, whose constructor runs
 *    `isSupportedInWeb().then(...)` with no `.catch` and calls `getMessaging()`
 *    inside it, which throws `messaging/unsupported-browser`. On web that call
 *    could never do anything useful, yet `authLoader.checkAuth()` fires it on
 *    every failed auth check and `session.logout()` on every sign-out — hence
 *    the desktop-only Sentry volume.
 * 2. **The bootstrap tier.** Both callers below (`@store/session`,
 *    `src/router/authLoader.ts`) sit in the entry graph, so the plugin shim,
 *    `@capacitor/app` and this module's whole subgraph landed in the cold-start
 *    closure for every visitor on every route. See `src/bootstrap/index.ts` and
 *    `scripts/measure-entry-graph.mjs`. (`firebase/messaging` itself was
 *    already lazy — the plugin loads its web impl via a dynamic import — so the
 *    byte win here is small; the rejection is the point.)
 *
 * Keep this file free of anything but the platform check: adding a re-export
 * here (e.g. `safeDispatchPushRoute`) puts the native module's whole subgraph
 * back in the entry chunk and undoes the fix.
 */

type NativeDispatchPushNotifications = Pick<
  typeof import("./pushNotificationsNative"),
  "revokeRegisteredDispatchPushDevice" | "startDispatchPushNotifications"
>;

export type DispatchPushFacadeDependencies = {
  isNativePlatform: () => boolean;
  loadNative: () => Promise<NativeDispatchPushNotifications>;
  logger: Pick<typeof safeConsole, "warn">;
};

export function createDispatchPushFacade({
  isNativePlatform,
  loadNative,
  logger,
}: DispatchPushFacadeDependencies) {
  /**
   * Release the device's push registration at sign-out. No-op on web, which
   * never registers one.
   *
   * Never rejects: callers fire this with a bare `void` on the logout path, so
   * a failed chunk load here must not surface as an unhandled rejection.
   */
  const revokeRegisteredDispatchPushDevice = async (
    fallback?: PushContext,
  ): Promise<void> => {
    if (!isNativePlatform()) return;
    try {
      const native = await loadNative();
      await native.revokeRegisteredDispatchPushDevice(fallback);
    } catch (error) {
      // The session is ending either way; a stale server-side device record is
      // recoverable (the breadcrumb survives for the next sign-out to retry).
      logger.warn(
        "authentication-lifecycle",
        "Dispatch push revoke unavailable",
        error,
      );
    }
  };

  /**
   * Register for dispatch pushes and return a listener cleanup. Resolves to a
   * no-op cleanup on web.
   */
  const startDispatchPushNotifications = async (
    context: PushContext,
  ): Promise<() => void> => {
    if (!isNativePlatform()) return () => undefined;
    const native = await loadNative();
    return native.startDispatchPushNotifications(context);
  };

  return {
    revokeRegisteredDispatchPushDevice,
    startDispatchPushNotifications,
  };
}

const dispatchPushFacade = createDispatchPushFacade({
  isNativePlatform: () => Capacitor.isNativePlatform(),
  loadNative: () => import("./pushNotificationsNative"),
  logger: safeConsole,
});

/**
 * Release the device's push registration at sign-out. No-op on web, which
 * never registers one.
 *
 * Never rejects: callers fire this with a bare `void` on the logout path, so a
 * failed chunk load here must not surface as an unhandled rejection.
 */
export const {
  revokeRegisteredDispatchPushDevice,
  startDispatchPushNotifications,
} = dispatchPushFacade;
